export const drag = {
  bind(el, binding, vnode) {
    // 存储拖拽状态
    el._dragState = {
      enabled: binding.value,
      isDragging: false,
      initialX: 0,
      initialY: 0,
      currentX: 0,
      currentY: 0,
      translateX: 0,
      translateY: 0,
      startTranslateX: 0,
      startTranslateY: 0,
      boundary: binding.modifiers.window
        ? "window"
        : binding.modifiers.boundary || binding.value?.boundary || "parent",
      minX: binding.modifiers.minX || -Infinity,
      maxX: binding.modifiers.maxX || Infinity,
      minY: binding.modifiers.minY || -Infinity,
      maxY: binding.modifiers.maxY || Infinity,
      boundaryEl: null,
      dragHandle: null, // 存储拖拽手柄元素
      handlersAttached: false, // 标记是否已添加事件处理
    };

    // 设置边界元素
    if (el._dragState.boundary === "parent") {
      el._dragState.boundaryEl = null; // bind 阶段先设为 null，inserted 阶段再赋值
    } else if (el._dragState.boundary === "window") {
      el._dragState.boundaryEl = null; // 用 null 标记 window
    } else if (typeof el._dragState.boundary === "string") {
      el._dragState.boundaryEl = document.querySelector(el._dragState.boundary);
    } else if (el._dragState.boundary instanceof HTMLElement) {
      el._dragState.boundaryEl = el._dragState.boundary;
    }

    // 添加状态更新函数
    el._updateDragState = (newValue) => {
      if (!el._dragState) return;

      el._dragState.enabled = newValue;

      // 如果状态变为可拖拽且未添加事件处理，则获取手柄并添加事件
      if (newValue) {
        attachDragHandlers();
      }
      // 如果状态变为不可拖拽且已添加事件处理，则移除事件
      else if (!newValue && el._dragState.handlersAttached) {
        detachDragHandlers();
      }

      // 更新手柄样式
      updateDragHandleStyle();
    };

    // 获取拖拽手柄
    const getDragHandle = () => {
      // if (el._dragState.dragHandle) return el._dragState.dragHandle;

      // 通过选择器查找手柄
      const dragHandle = el.querySelector(binding.arg || ".drag-handle");

      el._dragState.dragHandle = dragHandle;
      return dragHandle;
    };

    // 更新拖拽手柄样式
    const updateDragHandleStyle = () => {
      const dragHandle = getDragHandle();
      // console.log("__dragHandle", dragHandle);

      if (!dragHandle) return;

      // 根据拖拽状态显示或隐藏手柄
      // dragHandle.style.display = el._dragState.enabled ? "block" : "none";
      dragHandle.style.cursor = el._dragState.enabled ? "grab" : "default";
    };

    // 获取元素已有的transform值
    const getTransform = () => {
      const transform = window
        .getComputedStyle(el)
        .getPropertyValue("transform");
      const matrix = new DOMMatrix(transform);
      return { x: matrix.e, y: matrix.f };
    };

    // 应用范围限制
    const applyBounds = () => {
      // console.log("boundaryEl___", el._dragState.boundaryEl);

      const { startRect, startTranslateX, startTranslateY } = el._dragState;
      let minXBound, maxXBound, minYBound, maxYBound;
      if (el._dragState.boundary === "window") {
        // 以窗口为边界，盒子整体不能超出窗口
        minXBound = 0 - (startRect.left - startTranslateX);
        maxXBound = window.innerWidth - (startRect.right - startTranslateX);
        minYBound = 0 - (startRect.top - startTranslateY);
        maxYBound = window.innerHeight - (startRect.bottom - startTranslateY);
      } else if (el._dragState.boundaryEl) {
        const parentRect = el._dragState.boundaryEl.getBoundingClientRect();
        minXBound = parentRect.left - startRect.left + startTranslateX;
        maxXBound = parentRect.right - startRect.right + startTranslateX;
        minYBound = parentRect.top - startRect.top + startTranslateY;
        maxYBound = parentRect.bottom - startRect.bottom + startTranslateY;
      } else {
        minXBound = el._dragState.minX;
        maxXBound = el._dragState.maxX;
        minYBound = el._dragState.minY;
        maxYBound = el._dragState.maxY;
      }

      el._dragState.translateX = Math.max(
        minXBound,
        Math.min(el._dragState.translateX, maxXBound)
      );
      el._dragState.translateY = Math.max(
        minYBound,
        Math.min(el._dragState.translateY, maxYBound)
      );
    };

    // 开始拖拽
    const startDrag = (e) => {
      if (!el._dragState.enabled) return;

      el._dragState.isDragging = true;
      const transform = getTransform();
      el._dragState.startTranslateX = transform.x;
      el._dragState.startTranslateY = transform.y;
      el._dragState.startRect = el.getBoundingClientRect(); // 记录拖拽起始时的 rect

      if (e.type === "mousedown") {
        el._dragState.initialX = e.clientX;
        el._dragState.initialY = e.clientY;
      } else {
        el._dragState.initialX = e.touches[0].clientX;
        el._dragState.initialY = e.touches[0].clientY;
      }

      document.addEventListener("mousemove", dragHandler);
      document.addEventListener("mouseup", stopDrag);
      document.addEventListener("touchmove", dragHandler, { passive: false });
      document.addEventListener("touchend", stopDrag);

      // 更新手柄样式
      const dragHandle = getDragHandle();
      if (dragHandle) dragHandle.style.cursor = "grabbing";
      el.style.userSelect = "none";
      e.preventDefault();
    };

    // 拖拽处理
    const dragHandler = (e) => {
      if (!el._dragState.isDragging || !el._dragState.enabled) {
        stopDrag();
        return;
      }

      if (e.type === "mousemove") {
        el._dragState.currentX = e.clientX;
        el._dragState.currentY = e.clientY;
      } else {
        el._dragState.currentX = e.touches[0].clientX;
        el._dragState.currentY = e.touches[0].clientY;
      }

      el._dragState.translateX =
        el._dragState.startTranslateX +
        el._dragState.currentX -
        el._dragState.initialX;
      el._dragState.translateY =
        el._dragState.startTranslateY +
        el._dragState.currentY -
        el._dragState.initialY;

      applyBounds();

      el.style.transform = `translate(${el._dragState.translateX}px, ${el._dragState.translateY}px)`;
    };

    // 停止拖拽
    const stopDrag = () => {
      if (!el._dragState.isDragging) return;

      el._dragState.isDragging = false;

      // 更新手柄样式
      const dragHandle = getDragHandle();
      if (dragHandle)
        dragHandle.style.cursor = el._dragState.enabled ? "grab" : "default";
      el.style.userSelect = "";

      document.removeEventListener("mousemove", dragHandler);
      document.removeEventListener("mouseup", stopDrag);
      document.removeEventListener("touchmove", dragHandler);
      document.removeEventListener("touchend", stopDrag);
    };

    // 添加拖拽事件处理
    const attachDragHandlers = () => {
      const dragHandle = getDragHandle();
      if (!dragHandle) return;

      dragHandle.addEventListener("mousedown", startDrag);
      dragHandle.addEventListener("touchstart", startDrag, { passive: false });
      el._dragState.handlersAttached = true;
    };

    // 移除拖拽事件处理
    const detachDragHandlers = () => {
      const dragHandle = getDragHandle();
      if (!dragHandle || !el._dragState.handlersAttached) return;

      dragHandle.removeEventListener("mousedown", startDrag);
      dragHandle.removeEventListener("touchstart", startDrag);
      el._dragState.handlersAttached = false;

      // 如果正在拖拽，停止拖拽
      if (el._dragState.isDragging) {
        stopDrag();
      }
    };

    // 存储清理函数
    el._dragCleanup = () => {
      detachDragHandlers();
      delete el._dragCleanup;
      delete el._dragState;
      delete el._updateDragState;
    };

    // 初始更新状态
    if (el._updateDragState) {
      el._updateDragState(binding.value);
    }
  },

  inserted(el, binding) {
    // parent 边界在 inserted 阶段设置
    if (el._dragState && el._dragState.boundary === "parent") {
      el._dragState.boundaryEl = el.parentElement;
    }
    // 初始更新状态
    if (el._updateDragState) {
      setTimeout(() => {
        el._updateDragState(binding.value);
      }, 100);
    }
  },

  update(el, binding) {
    // console.log("djj_update", binding);

    // 当绑定值变化时更新状态
    if (binding.value !== binding.oldValue && el._updateDragState) {
      setTimeout(() => {
        el._updateDragState(binding.value);
      }, 100);
    }
  },

  unbind(el) {
    if (el._dragCleanup) {
      el._dragCleanup();
    }
  },
};
